import z from 'zod';

import { reportErrorToSentry } from '#configuration/setup/sentry.ts';

import { config } from '../../../config.ts';

const AddressSchema = z.object({
    label: z.string().optional(),
    countryCode: z.string().optional(),
    countryName: z.string().optional(),
    stateCode: z.string().optional(),
    state: z.string().optional(),
    countyCode: z.string().optional(),
    county: z.string().optional(),
    city: z.string().optional(),
    district: z.string().optional(),
    street: z.string().optional(),
    postalCode: z.string().optional(),
    houseNumber: z.string().optional(),
});

const PositionSchema = z.object({
    lat: z.number(),
    lng: z.number(),
});

export type HereGeocodePosition = { latitude: number; longitude: number };

const HereGeocodeItemSchema = z.object({
    address: AddressSchema.optional(),
    position: PositionSchema.optional(),
});

const HereGeocodeResponseSchema = z.object({
    items: z.array(HereGeocodeItemSchema).optional(),
});

const getAddressQuery = ({
    street,
    houseNumber,
    zipCode,
    city,
    country,
}: {
    street: string;
    houseNumber?: string;
    zipCode: string;
    city: string;
    country: string;
}) =>
    [
        [street, houseNumber].filter(Boolean).join(' ').trim(),
        [zipCode, city].filter(Boolean).join(' ').trim(),
        country.trim(),
    ]
        .filter(Boolean)
        .join(', ');

export type FetchHereGeocodePositionProps = {
    street: string;
    houseNumber?: string;
    zipCode: string;
    city: string;
    country: string;
};

export type HereGeocodeLocation = z.infer<typeof HereGeocodeItemSchema>;

export const fetchHereGeocodeLocation = async (query: string): Promise<HereGeocodeLocation | undefined> => {
    if (!query) {
        return undefined;
    }

    try {
        const searchParams = new URLSearchParams({
            q: query,
            apiKey: config.hereApiKey ?? '',
        });
        const response = await fetch(`https://geocode.search.hereapi.com/v1/geocode?${searchParams.toString()}`);

        const data = await response.json();
        const parsed = HereGeocodeResponseSchema.safeParse(data);
        if (!parsed.success) {
            console.error('[feature/location-geocode] Error parsing geocode response:', parsed.error);
            reportErrorToSentry(parsed.error);
            return undefined;
        }

        return parsed.data.items?.[0];
    } catch (error) {
        console.error('[feature/location-geocode] Error fetching geocode location:', error);
        reportErrorToSentry(error);
        return undefined;
    }
};

export const fetchHereGeocodePosition = async ({
    street,
    houseNumber,
    zipCode,
    city,
    country,
}: FetchHereGeocodePositionProps): Promise<HereGeocodePosition | undefined> => {
    const geocodeLocation = await fetchHereGeocodeLocation(
        getAddressQuery({ street, houseNumber, city, zipCode, country })
    );
    const firstPosition = geocodeLocation?.position;

    if (!firstPosition) {
        return undefined;
    }

    return { latitude: firstPosition.lat, longitude: firstPosition.lng };
};
